import { useState, useRef, useContext, useCallback, useEffect } from "react";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { Tooltip } from "@fluentui/react-components";
import { useFluentToast } from "../../../../../components/FluentUI/Fluent-Toast";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";
import { FluentInput } from "../../../../../components/FluentUI/Fluent-Input";
import PropTypes from "prop-types";
import {
  getLastUpdateTooltip,
  submitButton,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
  getEditedRows,
  mergeUpdatedEnergyData,
  buildEnergyPayload,
  getSummaryColumns,
  getDetailColumns,
  mapEnergyExportData,
  handleExport,
  SearchPromptMessage,
  getInitialDetailState,
  saveDetailStateToStorage,
  clearDetailStateFromStorage,
} from "./energy-banker-time-log-helper";
import EnergyBankerDetailGrid from "./energy-banker-detail-grid";
import EnergyBankerSummaryGrid from "./energy-banker-summary-grid";
import { SaveEnergyBankerHours, SaveEnergySIBankerHours } from "../../../../../services/api/dashboard-api";
import EnergySearchDialog from './global-search/search-dialog';
import "./energy-banker-time-log.scss";

const EnergyBankerTimeLog = ({ items, isSI = false }) => {
  const user = useContext(LoginContext);
  const [showDetails, setShowDetails] = useState(true);
  const [detailState, setDetailState] = useState(() => getInitialDetailState(user?.userID));
  const [isSaving, setIsSaving] = useState(false);
  const [data, setData] = useState(() => mergeUpdatedEnergyData(items.Projects || []));
  const [originalData, setOriginalData] = useState(data);
  const [searchValue, setSearchValue] = useState("");
  const [showGlobalSearchModal, setShowGlobalSearchModal] = useState(false);
  const [summaryItem, setSummaryItem] = useState(items);
  const [isVisibleDataEmpty, setIsVisibleDataEmpty] = useState(false);
  const { showToast, FluentToast } = useFluentToast();
  const _detailExport = useRef(null);
  const _summaryExport = useRef(null);
  const searchValueRef = useRef(searchValue);
  const history = useHistory();
  const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const [expandClass, setExpandClass] = useState("");
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value.trimStart());
  };

  useEffect(() => {
    searchValueRef.current = searchValue;
    if (searchValue?.trim()) {
      clearDetailStateFromStorage(user?.userID);
    }
  }, [searchValue]);

  const handleDetailStateChange = (newState) => {
    const userId = user?.userID;
    setDetailState(newState);
    saveDetailStateToStorage(userId, newState, searchValueRef.current);
  };

  const handleItemChange = (e) => {
    const { field, value, dataItem } = e;
    const restrictedFields = ["LastWeekHours", "WeekendHours", "NextWeekHours"];
    if (restrictedFields.includes(field) && value < 0) return;

    setData((prev) =>
      prev.map((row) =>
        row.ProjectId === dataItem.ProjectId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSubmit = async () => {
    const editedRows = getEditedRows(data, originalData);
    if (editedRows.length === 0) return;

    const payload = buildEnergyPayload(editedRows);
    setIsSaving(true);

    try {
      const saveEndpoint = isSI ? SaveEnergySIBankerHours : SaveEnergyBankerHours;
      const response = await saveEndpoint(payload);
      const updated = response?.data;

      if (!updated?.Projects?.length) {
        showToast("Save Failed", "Could not save your changes.", "error");
        return;
      }

      const merged = mergeUpdatedEnergyData(updated.Projects);
      setData(merged);
      setOriginalData(merged);
      setSummaryItem(updated);

      const now = new Date();
      showToast(
        "Edits Saved Successfully",
        `Changes saved on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })}`,
        "success"
      );
    } catch (err) {
      console.error("Failed to submit:", err);
      showToast(
        "Save Failed",
        "An unexpected error occurred. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleVisibleDataChange = useCallback(({ isEmpty, searchValue }) => {
    setIsVisibleDataEmpty(isEmpty && !!searchValue);
  }, []);

  const editedRows = getEditedRows(data, originalData);
  const isSubmitDisabled = isSaving || editedRows.length === 0;
  const tooltipText = getLastUpdateTooltip(summaryItem?.LastUpdated);
  const submitBtn = submitButton(isSubmitDisabled, handleSubmit);

  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;

  const canReset = detailState.sort.length > 0 || hasAnyFilter;
  const resetDataState = () => {
    setSearchValue("");
    const resetState = { sort: [], filter: null };
    setDetailState(resetState);
    clearDetailStateFromStorage(user?.userID);
  };

  const handleModifySearchFocus = () => {
    setTimeout(() => {
      const input = document.getElementById("energy-search-input");
      if (input) {
        input.focus();
      }
    }, 100);
  };
  const expandWidget = () => {
    setExpandClass(expandClass === "" ? "widgetExpanded" : "");
  };
  return (
    <>
      <div className={`energy-banker-time-log ${expandClass} dashboard-container-shadow`}>        
        <div className="widget-header">
          <div className="header-left">
            <div className="main-title-badge-row">
              <div className="main-title">Banker Time Log</div>
                <FluentInput
                  id="energy-search-input"
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  componentStyle="search-input"
                  contentBefore={<FluentUiIcon name="SearchRegular" size={14} />}
                  noKey
                />
            </div>
            <div className="sub-title">{isSI ? 'GIB/Energy/Summer Internship' : 'GIB/Energy'}</div>
          </div>
          <div className="header-right">
            <Tooltip content="Reset Filters and Sorting">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowResetRegular"
                    color={canReset ? "#0F6CBD" : "#A0A0A0"}
                    size={20}
                    disabled={!canReset}
                  />
                }
                disabled={!canReset}
                onClick={resetDataState}
              />
            </Tooltip>

            <Tooltip content="Export Data">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowDownloadRegular"
                    color="#0F6CBD"
                    size={20}
                  />
                }
                onClick={() =>
                  handleExport({ summaryRef: _summaryExport, detailRef: _detailExport })
                }
              />
            </Tooltip>
            <Tooltip content="Expand Widget">
              <span>
                <FluentButton
                  appearance="secondaryWithOutline"
                  icon={
                    <FluentUiIcon
                      name={
                        expandClass === "widgetExpanded"
                          ? "FullScreenMinimizeFilled"
                          : "FullScreenMaximizeFilled"
                      }
                      color={"#0F6CBD"}
                      size={20}
                    />
                  }
                  onClick={expandWidget}
                />
              </span>
            </Tooltip>
            <FluentButton
              text="Create Deal"
              appearance="secondaryWithOutline"
              disabled={!canCreate}
              icon={
                <FluentUiIcon
                  name="FolderAddRegular"
                  color={canCreate ? "#0F6CBD" : "#A0A0A0"}
                  size={20}
                />
              }
              onClick={() => {
                if (canCreate) {
                  history.push({
                    pathname: "/projects/create",
                    state: { fromDashboard: true },
                  });
                }
              }}
            />

            {tooltipText ? (
              <Tooltip content={tooltipText} relationship="label" positioning="below">
                <div>{submitBtn}</div>
              </Tooltip>
            ) : (
              submitBtn
            )}
          </div>
        </div>

        <ExcelExport data={[summaryItem]} ref={_summaryExport}>
          {getSummaryColumns()}
        </ExcelExport>

        {!searchValue && (
          <EnergyBankerSummaryGrid
            summaryItem={summaryItem}
            showDetails={showDetails}
            onToggle={() => setShowDetails(!showDetails)}
          />
        )}

         {showDetails && (
          <>
            <ExcelExport data={mapEnergyExportData(data)} ref={_detailExport}>
              {getDetailColumns()}
            </ExcelExport>

            <EnergyBankerDetailGrid
              data={data}
              onItemChange={handleItemChange}
              canEdit={canEdit}
              canCreate={canCreate}
              detailState={detailState}
              onDetailStateChange={handleDetailStateChange}
              searchValue={searchValue}
              onVisibleDataChange={handleVisibleDataChange}
              resetDataState={resetDataState}
              setShowGlobalSearchModal={setShowGlobalSearchModal}
            />

            {
              searchValue && !isVisibleDataEmpty && (
                <div className="widget-bottom-message">
                  <SearchPromptMessage
                    setShowGlobalSearchModal = {setShowGlobalSearchModal}
                  />
                </div>
              )
            }

            {/* Following div is added to bring focus to submit button if the user is tabbing through the widget */}
            <div
              tabIndex={0}
              style={{ width: 0, height: 0, overflow: "hidden" }}
              onFocus={() => {
                const btn = document.getElementById("tb-submit-btn");
                if (btn) btn.focus();
              }}
            />
          </>
        )}

      </div>
      {FluentToast}
      <EnergySearchDialog
        open={showGlobalSearchModal}
        searchValue={searchValue.trim()}
        onClose={() => setShowGlobalSearchModal(false)}
        onModifySearch={handleModifySearchFocus}
        isSI={isSI}
      />
    </>
  );
};

export default EnergyBankerTimeLog;

EnergyBankerTimeLog.propTypes = {
  items: PropTypes.shape({
    User: PropTypes.object,
    TotalHoursLastWeek: PropTypes.number,
    TotalWeekendHoursLastWeek: PropTypes.number,
    TotalHoursNextWeek: PropTypes.number,
    LastUpdated: PropTypes.string,
    Projects: PropTypes.arrayOf(
      PropTypes.shape({
        GroupName: PropTypes.string.isRequired,
        ProjectId: PropTypes.number.isRequired,
        ProjectIdString: PropTypes.string,
        TaskId: PropTypes.number,
        ProjectName: PropTypes.string.isRequired,
        IsOverdue: PropTypes.bool,
        Company: PropTypes.string,
        LastWeekHours: PropTypes.number,
        WeekendHours: PropTypes.number,
        ActualTotalHours: PropTypes.number,
        NextWeekHours: PropTypes.number,
        Members: PropTypes.array
      })
    ).isRequired,
  }).isRequired,
  isSI: PropTypes.bool,
};
